import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Badge, LoadingSkeleton, ToastContainer } from './ui';
import { PlusIcon } from '@heroicons/react/24/outline';
import { safeErrorMessage } from '../utils/apiError';
import { hasAnyRole } from '../utils/roles';

const paymentMethods = ['cash', 'ecocash', 'onemoney', 'bank_transfer', 'card'];

const CREATE_ROLES = ['administrator', 'principal'];

function Payments({ token, user }) {
  const canCreate = hasAnyRole(user, CREATE_ROLES);
  const [students, setStudents] = useState([]);
  const [payments, setPayments] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);

  const addToast = (message, type = 'success') => setToasts(prev => [...prev, { message, type }]);
  const removeToast = (index) => setToasts(prev => prev.filter((_, i) => i !== index));
  const [formData, setFormData] = useState({
    student_id: '', amount: '', currency: 'USD', payment_method: 'cash',
    transaction_reference: '', notes: ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [studentsRes, paymentsRes] = await Promise.all([
        fetch('/api/v1/students', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/v1/payments', { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      if (studentsRes.ok) setStudents(await studentsRes.json());
      if (paymentsRes.ok) setPayments(await paymentsRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const rateResponse = await fetch('/api/v1/exchange-rates/current', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const rateData = await rateResponse.json();

      const response = await fetch('/api/v1/payments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...formData, amount: parseFloat(formData.amount), rate_of_day: rateData.rate || 1.0 })
      });

      if (response.ok) {
        const payment = await response.json();
        addToast(`Payment recorded! Receipt #: ${payment.receipt_number}`, 'success');
        setShowForm(false);
        fetchData();
        setFormData({ student_id: '', amount: '', currency: 'USD', payment_method: 'cash', transaction_reference: '', notes: '' });
      } else {
        addToast(await safeErrorMessage(response, 'Failed to record payment'), 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      addToast('Network error. Please check your connection and try again.', 'error');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return '—'; }
  };

  const methodBadge = (method) => {
    const colors = { cash: 'success', ecocash: 'info', onemoney: 'warning', bank_transfer: 'default', card: 'primary' };
    return <Badge variant={colors[method] || 'default'}>{method?.toUpperCase() || 'N/A'}</Badge>;
  };

  const columns = [
    { header: 'Receipt #', render: (row) => <strong>{row.receipt_number || 'N/A'}</strong> },
    { header: 'Student', render: (row) => row.student_name || `ID: ${row.student_id}` },
    { header: 'Amount', render: (row) => `${row.currency === 'USD' ? '$' : 'ZWG '}${typeof row.amount === 'number' ? row.amount.toFixed(2) : row.amount}` },
    { header: 'Currency', accessor: 'currency' },
    { header: 'Method', render: (row) => methodBadge(row.payment_method) },
    { header: 'Date', render: (row) => formatDate(row.payment_date) },
    { header: 'Ref', render: (row) => row.transaction_reference || '—' },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--color-text)' }}>Payment Processing</h2>
        {canCreate && (
          <Button variant="primary" size="sm" onClick={() => setShowForm(!showForm)}>
            <PlusIcon style={{ width: 16, height: 16 }} />
            {showForm ? 'Cancel' : 'Record Payment'}
          </Button>
        )}
      </div>

      {canCreate && showForm && (
        <Card>
          <h3 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 600 }}>Record Payment</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Student *</label>
                <select value={formData.student_id} onChange={(e) => setFormData({...formData, student_id: e.target.value})} required>
                  <option value="">Select Student</option>
                  {students.map(s => <option key={s.id} value={s.id}>{s.full_name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Amount *</label>
                <input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({...formData, amount: e.target.value})} required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Currency *</label>
                <select value={formData.currency} onChange={(e) => setFormData({...formData, currency: e.target.value})}>
                  <option value="USD">USD</option>
                  <option value="ZWG">ZWG</option>
                </select>
              </div>
              <div className="form-group">
                <label>Payment Method *</label>
                <select value={formData.payment_method} onChange={(e) => setFormData({...formData, payment_method: e.target.value})}>
                  {paymentMethods.map(method => <option key={method} value={method}>{method.toUpperCase()}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Transaction Reference</label>
              <input type="text" value={formData.transaction_reference} onChange={(e) => setFormData({...formData, transaction_reference: e.target.value})} placeholder="EcoCash transaction ID" />
            </div>
            <div className="form-group">
              <label>Notes</label>
              <textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} rows="2" placeholder="Additional notes" />
            </div>
            <Button type="submit" variant="primary">Record Payment</Button>
          </form>
        </Card>
      )}

      <Card title="Recent Payments">
        {loading ? (
          <LoadingSkeleton type="text" rows={5} />
        ) : (
          <Table columns={columns} data={payments} emptyMessage="No payments recorded yet." />
        )}
      </Card>

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
}

export default Payments;
